// Preview as firm: the firm's migration status page exactly as they see it, read-only. The page is inert
// (nothing in it can be clicked, typed into or focused), so no firm action can run from a staff session.
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import type { StatusPage as StatusData } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { Banner, ButtonLink, Spinner } from '../ui';
import { StatusView } from '../status/StatusPage';

export function FirmPreviewPage() {
  const { id = '' } = useParams();
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<StatusData>(`/api/admin/onboardings/${id}/firm-view`)); setError(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not load the firm’s view.'); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const submitted = data?.steps.find((s) => s.id === 'plan.intake')?.state === 'done';
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span aria-hidden="true" className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand-soft text-brand"><Eye size={18} /></span>
          <div className="min-w-0">
            <h1 className="m-0 text-[20px] font-bold leading-tight">Preview as firm{data ? `: ${data.firmName}` : ''}</h1>
            <p className="m-0 text-[13px] text-muted">Their migration status page, exactly as they see it. Read-only: nothing here can be clicked.</p>
          </div>
        </div>
        <ButtonLink to={`/admin/onboarding/${id}`} variant="secondary" size="sm"><ArrowLeft aria-hidden size={16} />Back to the review</ButtonLink>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {!data && !error && <Spinner label="Loading the firm’s view" />}
      {data && !submitted && (
        <Banner tone="info">They haven’t submitted the questionnaire yet, so their link still opens the questionnaire. This is the status page they land on after.</Banner>
      )}
      {data && (
        <div
          ref={(el) => { el?.setAttribute('inert', ''); }}
          aria-label={`${data.firmName}'s status page (preview)`}
          role="region"
          data-testid="firm-preview"
          className="select-none overflow-hidden rounded-2xl border-2 border-dashed border-line-btn"
        >
          <StatusView data={data} onReload={load} onMessage={() => {}} />
        </div>
      )}
    </>
  );
}
